import { jwtDecode } from 'jwt-decode';

import apiClient from '@api/apiClient';
import apiProfile from '@api/apiProfile';
import { buildAuthNavigationPath, getSafeRedirectPath } from '@AuthCommon/utils';
import { CACHE_KEYS, clearCachedData } from '@utils/settingsCache';
import { updateLocalStorage } from '@utils/utils';
import Mixpanel from '@analytics/mixpanel';

export default class Auth {
  constructor() {
    this.setSession = this.setSession.bind(this);
    this.handlePartnerRedirectToken = this.handlePartnerRedirectToken.bind(this);
    this.openPartnerRedirect = this.openPartnerRedirect.bind(this);
    this.renewSession = this.renewSession.bind(this);
    this.logout = this.logout.bind(this);
    this.isAdmin = this.isAdmin.bind(this);
  }

  async setSession(accessToken, useRedirect = false) {
    const payload = jwtDecode(accessToken);
    const { expires_at, name, email, picture, email_verified, to_email, roles, region, ie, id, kyc, redirected_from_partner, ui } = payload || {};

    apiClient.defaults.headers.common = { Authorization: `Bearer ${accessToken}` };

    if (redirected_from_partner) {
      try {
        const { data: { config } } = await apiProfile.fetchUserConfig();
        localStorage.setItem('userConfig', JSON.stringify(config));

        const { default: globalConfig } = await import('react-global-configuration');
        globalConfig.set(config, { assign: true });

        const { loadTheme } = await import('@utils/loadTheme');
        await loadTheme();
      } catch (error) {
        console.error('Failed to fetch user config:', error);
      }
    }

    localStorage.setItem('isLoggedIn', '1');
    localStorage.setItem('expiresAt', expires_at * 1000);
    localStorage.setItem('accessToken', accessToken);
    updateLocalStorage('name', name);
    updateLocalStorage('email', email);
    updateLocalStorage('toEmail', to_email);
    updateLocalStorage('emailVerified', email_verified);
    updateLocalStorage('id', id);
    updateLocalStorage('avatar', picture);
    updateLocalStorage('redirected_from_partner', redirected_from_partner);
    updateLocalStorage('roles', roles ? roles.join() : null);
    updateLocalStorage('re', region);
    updateLocalStorage('ie', ie);
    updateLocalStorage('uiDomain', ui);

    Mixpanel.identify(id);

    if (useRedirect) {
      if (kyc) {
        localStorage.setItem('kyc', 1);
        window.location = '/signup-confirm';
      } else {
        const redirect = getSafeRedirectPath(localStorage.getItem('redirect'));
        localStorage.removeItem('redirect');
        window.location = redirect || '/';
      }
    }
  }

  async handlePartnerRedirectToken(token) {
    const { data } = await apiProfile.authPartnerRedirectToken(token);
    this.setSession(data.access_token, true);
  }

  async openPartnerRedirect(redirectDomain, redirectToken) {
    const storedRedirect = localStorage.getItem('redirect');
    const redirect = getSafeRedirectPath(storedRedirect);
    if (storedRedirect && !redirect) localStorage.removeItem('redirect');

    const partnerRedirectDomain = `${window.location.protocol}//${redirectDomain}`;
    let redirectUrl = `${partnerRedirectDomain}/partner-redirect/${redirectToken}/`;
    if (redirect) {
      redirectUrl += `?redirect=${encodeURIComponent(redirect)}`;
    }

    window.location.replace(redirectUrl);
  }

  async renewSession() {
    try {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        this.logout();
        return false;
      }

      const { data } = await apiProfile.refreshAuthToken(accessToken);
      this.setSession(data.access_token);
      return true;
    } catch (error) {
      console.error('Failed to renew session:', error.message);
      return false;
    }
  }

  logout(email=null) {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('redirect');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('toEmail');
    localStorage.removeItem('avatar');
    localStorage.removeItem('redirected_from_partner');
    localStorage.removeItem('userConfig');
    localStorage.removeItem('roles');
    localStorage.removeItem('ie');
    localStorage.removeItem('uiDomain');
    localStorage.removeItem('re');
    localStorage.removeItem('emailVerified');
    localStorage.removeItem('id');
    localStorage.removeItem('provider');
    localStorage.removeItem('googleIntegration');
    localStorage.removeItem('paidUser');
    Object.values(CACHE_KEYS).forEach(clearCachedData);

    if (email) window.location = buildAuthNavigationPath('/login', '', email);
    else window.location = '/login';
  }

  isAdmin() {
    if (this.roles) {
      return this.roles.includes('admin');
    }
    return false;
  }

  get authenticated() {
    const expiresAt = localStorage.getItem('expiresAt');
    if (expiresAt) {
      return new Date().getTime() < Number(expiresAt);
    }

    return false;
  }

  get profile() {
    let region = localStorage.getItem('re');

    return {
      name: localStorage.getItem('name'),
      email: localStorage.getItem('email'),
      userId: localStorage.getItem('id'),
      uiDomain: localStorage.getItem('uiDomain'),
      avatar: localStorage.getItem('avatar'),
      redirected_from_partner: localStorage.getItem('redirected_from_partner'),
      roles: this.roles,
      emailVerified: localStorage.getItem('emailVerified') === 'true',
      ie: localStorage.getItem('ie'),
      provider: localStorage.getItem('provider'),
      region: region === 'undefined' ? null : region,
    };
  }

  get roles() {
    const value = localStorage.getItem('roles');
    return value ? value.split(',') : [];
  }
}
